// Client-side guards for the country form.
//
// The countries endpoint writes straight into Odoo's `res.country`, whose
// columns are stricter than the contacts API JSON schema (which types every
// field as `string | null`). Sending a value Odoo cannot cast surfaces as a
// raw 500 in the UI, e.g. a calling code of "WAC" comes back as
// `invalid literal for int() with base 10: 'WAC'`. These rules mirror the
// Odoo column definitions so the user gets a field-level message instead:
//
//   name       Char, required
//   code       Char(2), unique, upper-cased by Odoo on create
//   phone_code Integer
//   vat_label  Char
//   currency   resolved by name against res.currency (ISO 4217 code)

const COUNTRY_LIMITS = {
  NAME_MAX_LENGTH: 100,
  CODE_LENGTH: 2,
  CURRENCY_LENGTH: 3,
  PHONE_CODE_MAX_DIGITS: 4,
  VAT_LABEL_MAX_LENGTH: 50,
};

const CODE_PATTERN = /^[A-Za-z]{2}$/;
const CURRENCY_PATTERN = /^[A-Za-z]{3}$/;
const PHONE_CODE_PATTERN = /^\+?\d+$/;

const trim = (value) => (typeof value === 'string' ? value.trim() : '');

function validateName(value) {
  if (!value) return 'Country Name is required';
  if (value.length > COUNTRY_LIMITS.NAME_MAX_LENGTH) {
    return `Country Name cannot exceed ${COUNTRY_LIMITS.NAME_MAX_LENGTH} characters`;
  }
  return null;
}

function validateCode(value) {
  if (!value) return null;
  if (!CODE_PATTERN.test(value)) {
    return 'Country Code must be exactly 2 letters (ISO 3166-1 alpha-2), e.g. AT';
  }
  return null;
}

function validateCurrency(value) {
  if (!value) return null;
  if (!CURRENCY_PATTERN.test(value)) {
    return 'Currency must be a 3-letter ISO 4217 code, e.g. EUR';
  }
  return null;
}

function validatePhoneCode(value) {
  if (!value) return null;
  if (!PHONE_CODE_PATTERN.test(value)) {
    return 'Country Calling Code must contain digits only, e.g. 43';
  }
  const digits = value.replace(/^\+/, '');
  if (Number(digits) === 0) {
    return 'Country Calling Code must be greater than 0';
  }
  if (digits.replace(/^0+/, '').length > COUNTRY_LIMITS.PHONE_CODE_MAX_DIGITS) {
    return `Country Calling Code cannot exceed ${COUNTRY_LIMITS.PHONE_CODE_MAX_DIGITS} digits`;
  }
  return null;
}

function validateVatLabel(value) {
  if (value.length > COUNTRY_LIMITS.VAT_LABEL_MAX_LENGTH) {
    return `VAT Label cannot exceed ${COUNTRY_LIMITS.VAT_LABEL_MAX_LENGTH} characters`;
  }
  return null;
}

// Returns a `{field: message}` map holding one entry per invalid field, so the
// form can flag every problem in a single pass. An empty object means the
// payload is safe to send.
function validateCountry(formData = {}) {
  const candidates = {
    name: validateName(trim(formData.name)),
    code: validateCode(trim(formData.code)),
    currency: validateCurrency(trim(formData.currency)),
    phoneCode: validatePhoneCode(trim(formData.phoneCode)),
    vatLabel: validateVatLabel(trim(formData.vatLabel)),
  };

  return Object.entries(candidates).reduce(
    (errors, [field, message]) => (message ? {...errors, [field]: message} : errors),
    {},
  );
}

// Builds the request payload: trims everything, applies the same upper-casing
// Odoo does on create so the value the user sees matches what gets stored, and
// turns blank optional fields into an explicit null (the API schema accepts
// `string | null`, and Odoo maps null to a cleared field).
function normalizeCountry(formData = {}) {
  const code = trim(formData.code);
  const currency = trim(formData.currency);
  const phoneCode = trim(formData.phoneCode).replace(/^\+/, '').replace(/^0+(?=\d)/, '');
  const vatLabel = trim(formData.vatLabel);

  return {
    name: trim(formData.name),
    code: code ? code.toUpperCase() : null,
    currency: currency ? currency.toUpperCase() : null,
    phoneCode: phoneCode || null,
    vatLabel: vatLabel || null,
  };
}

export {COUNTRY_LIMITS, normalizeCountry, validateCountry};
